'use client';

import { Button, Input, message, Typography } from 'antd';
import { useFormik } from 'formik';
import { useState } from 'react';
import { emailSchema, passwordSchema } from 'src/validates';

import { ArrowLeftOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';

import { OtpVerification } from './OtpVertification';

const { Text, Paragraph } = Typography;

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Step 1: Email form
  const emailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: emailSchema,
    onSubmit: async (values) => {
      setIsLoading(true);

      // Mô phỏng gửi OTP (chỉ UI)
      setTimeout(() => {
        setUserEmail(values.email);
        message.success('Mã OTP đã được gửi đến email của bạn');
        setStep(2);
        setIsLoading(false);
      }, 1500);
    },
  });

  // Step 3: New password form
  const passwordFormik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async () => {
      setIsLoading(true);

      // Mô phỏng đặt lại mật khẩu (chỉ UI)
      setTimeout(() => {
        message.success('Mật khẩu của bạn đã được đặt lại thành công');
        onBack(); // Quay lại form đăng nhập
        setIsLoading(false);
      }, 1500);
    },
  });

  // Xử lý khi xác thực OTP thành công
  const handleOtpSuccess = () => {
    setStep(3);
  };

  // Xử lý khi quay lại từ OTP
  const handleBackFromOtp = () => {
    setStep(1);
  };

  // Step 1: Email form
  if (step === 1) {
    return (
      <div className="forgot-password-container">
        <div style={{ marginBottom: 16 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ padding: '0 8px 0 0' }}
          />
          <Text strong style={{ fontSize: 16 }}>
            Quên mật khẩu
          </Text>
        </div>

        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Nhập địa chỉ email của bạn và chúng tôi sẽ gửi mã OTP để đặt lại mật khẩu.
        </Paragraph>

        <form onSubmit={emailFormik.handleSubmit} className="space-y-4">
          <div className="mb-4">
            <div className="text-sm font-medium mb-1">Email</div>
            <Input
              id="email"
              name="email"
              className="!h-12"
              prefix={<MailOutlined />}
              placeholder="your@email.com"
              value={emailFormik.values.email}
              onChange={emailFormik.handleChange}
              onBlur={emailFormik.handleBlur}
              status={emailFormik.touched.email && emailFormik.errors.email ? 'error' : ''}
            />
            {emailFormik.touched.email && emailFormik.errors.email && (
              <div className="text-red-500 text-sm mt-1">{emailFormik.errors.email}</div>
            )}
          </div>
          <Button
            type="primary"
            htmlType="submit"
            className="!h-12"
            loading={isLoading}
            disabled={!emailFormik.isValid || emailFormik.isSubmitting}
            block
          >
            {isLoading ? 'Đang xử lý...' : 'Tiếp tục'}
          </Button>
        </form>
      </div>
    );
  }

  // Step 2: OTP verification
  if (step === 2) {
    return (
      <OtpVerification
        email={userEmail}
        onVerificationSuccess={handleOtpSuccess}
        onBack={handleBackFromOtp}
      />
    );
  }

  // Step 3: New password form
  return (
    <div className="reset-password-container">
      <div style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => setStep(2)}
          style={{ padding: '0 8px 0 0' }}
        />
        <Text strong style={{ fontSize: 16 }}>
          Đặt lại mật khẩu
        </Text>
      </div>

      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Nhập mật khẩu mới cho tài khoản của bạn.
      </Paragraph>

      <form onSubmit={passwordFormik.handleSubmit} className="space-y-4">
        <div className="form-item">
          <div className="form-label">Mật khẩu mới</div>
          <Input.Password
            id="password"
            name="password"
            prefix={<LockOutlined />}
            value={passwordFormik.values.password}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            status={
              passwordFormik.touched.password && passwordFormik.errors.password ? 'error' : ''
            }
          />
          {passwordFormik.touched.password && passwordFormik.errors.password && (
            <div className="error-message">{passwordFormik.errors.password}</div>
          )}
        </div>

        <div className="form-item">
          <div className="form-label">Xác nhận mật khẩu mới</div>
          <Input.Password
            id="confirmPassword"
            name="confirmPassword"
            prefix={<LockOutlined />}
            value={passwordFormik.values.confirmPassword}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            status={
              passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword
                ? 'error'
                : ''
            }
          />
          {passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword && (
            <div className="error-message">{passwordFormik.errors.confirmPassword}</div>
          )}
        </div>

        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={!passwordFormik.isValid || passwordFormik.isSubmitting}
          block
        >
          {isLoading ? 'Đang xử lý...' : 'Đặt lại mật khẩu'}
        </Button>
      </form>
    </div>
  );
}
